/**
 * DevicesPage - Página de Dispositivos Premium
 * Migrado para usar componentes genéricos Premium
 */

import {
 useState, useMemo, useCallback, useEffect 
} from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useDevices, useDevicesFilters } from '../common/hooks';
import { useTableSelection } from '../common/hooks';
import {
  Box,
  Stack,
  Button,
  Typography,
  useTheme,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  TextField,
  InputAdornment,
  CircularProgress,
  Tooltip,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  IconButton,
  useMediaQuery,
  Checkbox,
  Menu,
} from '@mui/material';
import { usePremiumTokens } from '../common/theme/usePremiumTokens';
import SearchIcon from '@mui/icons-material/Search';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import PeopleIcon from '@mui/icons-material/People';
import PersonIcon from '@mui/icons-material/Person';
import FolderIcon from '@mui/icons-material/Folder';
import RefreshIcon from '@mui/icons-material/Refresh';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StorageIcon from '@mui/icons-material/Storage';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HelpIcon from '@mui/icons-material/Help';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import LinkIcon from '@mui/icons-material/Link';

import { useTranslation } from '../common/components/LocalizationProvider';
import PageLayout from '../common/components/PageLayout';
import SettingsMenu from './components/SettingsMenu';
import { useManager, useDeviceReadonly } from '../common/util/permissions';
import {
  formatAddress,
  formatStatus,
  formatTime,
} from '../common/util/formatter';
import { usePreference } from '../common/util/preferences';
import useCrudExports from '../common/hooks/useCrudExports';

// Componentes Premium Genéricos
import {
  PremiumStatCards,
  PremiumEmptyState,
  PremiumLoadingSkeleton,
  BulkActionsBar,
  BulkDeleteDialog,
  BulkConnectionsDialog,
} from '../common/components/premium';

// Componentes específicos de Devices (não migráveis)
import DevicesTableRow from './components/DevicesTableRow';

const DevicesPage = () => {
  const navigate = useNavigate();
  const t = useTranslation();
  const theme = useTheme();
  const tokens = usePremiumTokens();
  const manager = useManager();
  const deviceReadonly = useDeviceReadonly();
  const coordinateFormat = usePreference('coordinateFormat');

  // Responsive breakpoints
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Redux - apenas groups para o filtro
  const groups = useSelector((state) => state.groups.items, shallowEqual);

  // HOOKS - Gerenciamento de dispositivos
  const {
    devices,
    stats,
    loading,
    refreshing,
    refresh,
    bulkDelete,
    bulkMoveToGroup,
    fetchDriverLinksForDevices,
    clearDriverLinksCache,
  } = useDevices();

  const {
    statusFilter,
    setStatusFilter,
    groupFilter,
    setGroupFilter,
    searchText,
    setSearchText,
    orderBy,
    order,
    handleSort,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    filteredDevices,
    paginatedDevices,
    hasActiveFilters,
    clearFilters,
  } = useDevicesFilters(devices);

  // Hook genérico de seleção (migrado de useDevicesSelection)
  const {
    selectedIds,
    selectedCount,
    toggleSelect,
    selectAll,
    clearSelection,
    isSelected,
    isAllSelected,
    isIndeterminate,
    hasSelection,
  } = useTableSelection(filteredDevices);

  // Fetch driver links for visible devices (lazy loading)
  useEffect(() => {
    if (paginatedDevices.length > 0) {
      const deviceIds = paginatedDevices.map((d) => d.id);
      fetchDriverLinksForDevices(deviceIds);
    }
  }, [paginatedDevices, fetchDriverLinksForDevices]);

  // Bulk action dialogs
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [moveMenuAnchor, setMoveMenuAnchor] = useState(null);
  const [connectionsDialogOpen, setConnectionsDialogOpen] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  // Export menu
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);

  // Available groups for filter
  const availableGroups = useMemo(() => Object.values(groups).sort((a, b) => a.name.localeCompare(b.name)), [groups]);

  // StatCards filters (migrado de DevicesStatCards)
  const statFilters = useMemo(() => [
    { value: '', label: t('sharedAll'), count: stats.total, color: tokens?.accent?.primary, Icon: StorageIcon },
    { value: 'online', label: t('deviceStatusOnline'), count: stats.online, color: tokens?.status?.online, Icon: CheckCircleIcon },
    { value: 'offline', label: t('deviceStatusOffline'), count: stats.offline, color: tokens?.status?.offline, Icon: ErrorIcon },
    { value: 'unknown', label: t('deviceStatusUnknown'), count: stats.unknown, color: tokens?.status?.idle, Icon: HelpIcon },
  ], [t, stats, tokens]);

  // Handler para mudança de filtro de status
  const handleFilterChange = useCallback((value) => {
    setStatusFilter(value);
    setPage(0);
  }, [setStatusFilter, setPage]);

  // Bulk delete handler
  const handleBulkDelete = useCallback(async () => {
    setBulkActionLoading(true);
    try {
      await bulkDelete(selectedIds);
      clearSelection();
      setDeleteDialogOpen(false);
    } finally {
      setBulkActionLoading(false);
    }
  }, [selectedIds, bulkDelete, clearSelection]);

  // Bulk move handler
  const handleBulkMoveToGroup = useCallback(async (groupId) => {
    setBulkActionLoading(true);
    try {
      await bulkMoveToGroup(selectedIds, groupId);
      clearSelection();
      setMoveMenuAnchor(null);
    } catch (error) {
      console.error('Erro ao mover dispositivos:', error);
    } finally {
      setBulkActionLoading(false);
    }
  }, [selectedIds, bulkMoveToGroup, clearSelection]);

  // Custom actions para BulkActionsBar
  const bulkCustomActions = useMemo(() => [
    {
      key: 'moveToGroup',
      label: t('sharedMoveToGroup') || 'Mover para Grupo',
      icon: <DriveFileMoveIcon />,
      onClick: (e) => setMoveMenuAnchor(e.currentTarget),
    },
    {
      key: 'connections',
      label: t('sharedConnections') || 'Conexões',
      icon: <LinkIcon />,
      onClick: () => setConnectionsDialogOpen(true),
    },
  ], [t]);

  // Export columns
  const exportColumns = [
    { field: 'id', headerNameKey: 'sharedId' },
    { field: 'name', headerNameKey: 'sharedName' },
    { field: 'uniqueId', headerNameKey: 'deviceIdentifier' },
    {
      field: 'groupId',
      headerNameKey: 'groupParent',
      valueGetter: ({ row }) => row.groupName || '',
    },
    {
      field: 'driverName',
      headerNameKey: 'sharedDriver',
      valueGetter: ({ row }) => row.driverName || '',
    },
    {
      field: 'plate',
      headerNameKey: 'sharedDevicePlate',
      valueGetter: ({ row }) => row.attributes?.form?.plate || '',
    },
    { field: 'model', headerNameKey: 'deviceModel' },
    { field: 'phone', headerNameKey: 'sharedPhone' },
    { field: 'contact', headerNameKey: 'deviceContact' },
    {
      field: 'iccid',
      headerNameKey: 'sharedIMEIChip',
      valueGetter: ({ row }) => row.attributes?.iccid || '',
    },
    {
      field: 'speed',
      headerNameKey: 'positionSpeed',
      valueGetter: ({ row }) => Math.round((row.speed || 0) * 1.852),
    },
    {
      field: 'ignition',
      headerNameKey: 'positionIgnition',
      valueGetter: ({ row }) => row.ignition === true ? 'Ligada' : row.ignition === false ? 'Desligada' : '-',
    },
    {
      field: 'blocked',
      headerNameKey: 'positionBlocked',
      valueGetter: ({ row }) => row.blocked === true ? 'Sim' : row.blocked === false ? 'Não' : '-',
    },
    {
      field: 'status',
      headerNameKey: 'deviceStatus',
      valueGetter: ({ row }) => formatStatus(row.status, t),
    },
    {
      field: 'lastUpdate',
      headerNameKey: 'deviceLastUpdate',
      valueGetter: ({ row }) => formatTime(row.lastUpdate, 'minutes'),
    },
    {
      field: 'address',
      headerNameKey: 'positionAddress',
      valueGetter: ({ row }) => row.address || '',
    },
  ];

  const { handleExportCsv, handleExportXlsx, handleExportPdf } = useCrudExports({
    titleKey: 'reportDevicesTitle',
    headerKey: 'reportDevicesHeader',
    rows: filteredDevices,
    columns: exportColumns,
    theme,
    t,
    options: {
      includeWatermark: true,
    },
  });

  // Skeleton columns configuration for loading state
  const skeletonColumns = useMemo(() => [
    { type: 'checkbox' },
    { type: 'avatarSmall' },
    { type: 'textShort' },
    { type: 'avatarWithText' },
    { type: 'text' },
    { type: 'text' },
    { type: 'text' },
    { type: 'chip' },
    { type: 'indicators' },
    { type: 'text' },
    ...(manager ? [{ type: 'text' }] : []),
    { type: 'actions', count: 2 },
  ], [manager]);

  // Loading skeleton - show only on initial load
  if (loading && devices.length === 0) {
    return (
      <PageLayout menu={<SettingsMenu />} breadcrumbs={['settingsTitle', 'sharedDevice']}>
        <Box sx={{ p: { xs: 1, sm: 1.5, md: 2 } }}>
          <PremiumLoadingSkeleton
            rows={10}
            columns={skeletonColumns}
            showStats
            statsCount={4}
            showFilters
            showGroupFilter
            wrapInPaper
          />
        </Box>
      </PageLayout>
    );
  }

  return (
    <PageLayout menu={<SettingsMenu />} breadcrumbs={['settingsTitle', 'sharedDevice']}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minHeight: 'calc(100vh - 64px)',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            flex: 1,
            p: { xs: 1, sm: 1.5, md: 2 },
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: 1, sm: 1.5 },
            overflow: 'hidden',
            minHeight: 0,
          }}
        >
          {/* Statistics Cards - Componente Genérico */}
          <PremiumStatCards
            filters={statFilters}
            activeFilter={statusFilter}
            onFilterChange={handleFilterChange}
          />

          {/* Search and Filters Bar */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: { xs: 1, sm: 1.5 },
              alignItems: { xs: 'stretch', md: 'center' },
              flexShrink: 0,
            }}
          >
            {/* Search and Group Filter */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 1,
                flex: 1,
              }}
            >
              <TextField
                size="small"
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value);
                  setPage(0);
                }}
                placeholder={t('sharedSearchPlaceholder') || 'Buscar por nome, placa, motorista, grupo...'}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: tokens.accent.primary }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  flex: { xs: 1, sm: 2, md: 3 },
                  minWidth: { xs: '100%', sm: 200 },
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: tokens?.surface?.glass,
                    backdropFilter: 'blur(12px)',
                    borderRadius: tokens?.radius?.md,
                    transition: tokens?.transitions?.all,
                    '& fieldset': {
                      borderColor: tokens?.border?.subtle,
                    },
                    '&:hover fieldset': {
                      borderColor: tokens?.accent?.primary,
                    },
                    '&.Mui-focused': {
                      boxShadow: `0 0 0 3px ${tokens?.accent?.glow}`,
                      '& fieldset': {
                        borderColor: tokens?.accent?.primary,
                        borderWidth: '1px',
                      },
                    },
                  },
                }}
              />

              {/* Group Filter */}
              <FormControl
                size="small"
                sx={{
                  flex: { xs: 1, sm: 1 },
                  minWidth: { xs: '100%', sm: 180 },
                  maxWidth: { sm: 250 },
                }}
              >
                <InputLabel sx={{ color: tokens.text.secondary }}>{t('groupParent') || 'Grupo'}</InputLabel>
                <Select
                  value={groupFilter}
                  label={t('groupParent') || 'Grupo'}
                  onChange={(e) => { setGroupFilter(e.target.value); setPage(0); }}
                  sx={{
                    backgroundColor: tokens?.surface?.glass,
                    backdropFilter: 'blur(12px)',
                    borderRadius: tokens?.radius?.md,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: tokens?.border?.subtle,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: tokens?.accent?.primary,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: tokens?.accent?.primary,
                      borderWidth: '1px',
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        backgroundColor: tokens?.surface?.overlay,
                        backdropFilter: 'blur(16px)',
                        border: `1px solid ${tokens?.border?.default}`,
                        borderRadius: tokens?.radius?.lg,
                        mt: 0.5,
                      },
                    },
                  }}
                >
                  <MenuItem value="all">{t('sharedAll') || 'Todos os Grupos'}</MenuItem>
                  {availableGroups.map((group) => (
                    <MenuItem key={group.id} value={group.id}>
                      {group.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Action Buttons */}
            <Stack
              direction="row"
              spacing={0.5}
              sx={{
                flexShrink: 0,
                flexWrap: 'wrap',
                gap: 0.5,
                justifyContent: { xs: 'flex-start', md: 'flex-end' },
              }}
            >
              <Tooltip title={t('sharedRefresh') || 'Atualizar'}>
                <span>
                  <IconButton
                    onClick={refresh}
                    disabled={loading || refreshing}
                    size="small"
                    sx={{
                      bgcolor: tokens.surface.glass,
                      borderRadius: tokens.radius.md,
                      transition: tokens.transitions.all,
                      '&:hover': {
                        bgcolor: tokens.accent.subtle,
                        color: tokens.accent.primary,
                      },
                    }}
                  >
                    <RefreshIcon sx={{ animation: refreshing ? 'spin 1s linear infinite' : 'none', '@keyframes spin': { '100%': { transform: 'rotate(360deg)' } } }} />
                  </IconButton>
                </span>
              </Tooltip>
              <Button
                variant="contained"
                size="small"
                startIcon={!isMobile && <AddCircleIcon />}
                onClick={() => navigate('/settings/device')}
                disabled={deviceReadonly}
                sx={{
                  minWidth: isMobile ? 40 : 'auto',
                  px: isMobile ? 1 : 2,
                  bgcolor: tokens.accent.primary,
                  color: tokens.isDark ? tokens.text.inverse : '#ffffff',
                  borderRadius: tokens.radius.md,
                  fontWeight: 500,
                  textTransform: 'none',
                  transition: tokens.transitions.all,
                  '&:hover': {
                    bgcolor: tokens?.accent?.primaryHover,
                    transform: 'translateY(-1px)',
                    boxShadow: tokens?.shadows?.glowAccent,
                  },
                }}
              >
                {isMobile ? <AddCircleIcon /> : t('sharedAdd')}
              </Button>
              {manager && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<FileDownloadIcon />}
                  endIcon={<ExpandMoreIcon />}
                  onClick={(e) => setExportMenuAnchor(e.currentTarget)}
                  sx={{
                    minWidth: isMobile ? 40 : 'auto',
                    color: tokens.text.primary,
                    borderColor: tokens.border.default,
                    borderRadius: tokens.radius.md,
                    fontWeight: 500,
                    textTransform: 'none',
                    transition: tokens.transitions.all,
                    '&:hover': {
                      borderColor: tokens.accent.primary,
                      bgcolor: `${tokens.accent.primary}10`,
                    },
                  }}
                >
                  {isMobile ? '' : (t('reportExport') || 'Exportar')}
                </Button>
              )}
            </Stack>
          </Box>

          {/* Results count & Bulk Actions Bar */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 1, sm: 2 },
              flexWrap: { xs: 'wrap', sm: 'nowrap' },
              minWidth: 0,
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              {filteredDevices.length} {t('sharedDevices') || 'dispositivos'}
              {hasActiveFilters && ` (${t('sharedFiltered') || 'filtrado'})`}
            </Typography>

            {/* Bulk Actions - Componente Genérico */}
            {hasSelection && (
              <BulkActionsBar
                selectedCount={selectedCount}
                onClearSelection={clearSelection}
                onDelete={() => setDeleteDialogOpen(true)}
                deleteDisabled={deviceReadonly}
                loading={bulkActionLoading}
                entityName={t('sharedDevices') || 'dispositivo(s)'}
                customActions={bulkCustomActions}
              />
            )}
          </Box>

          {/* Table */}
          <Paper
            sx={{
              flex: 1,
              overflow: 'hidden',
              backgroundColor: tokens?.surface?.glass,
              backdropFilter: 'blur(12px)',
              borderRadius: tokens?.radius?.lg,
              border: `1px solid ${tokens?.border?.subtle}`,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              // Ensure the Paper fills remaining space
              height: '100%',
            }}
          >
            <TableContainer
              className="premium-table-container"
              sx={{
                flex: 1,
                minHeight: 0,
                overflow: 'auto',
                // Let flex handle the height - no fixed maxHeight
                // Scrollbar styling
                '&::-webkit-scrollbar': {
                  width: 8,
                  height: 8,
                },
                '&::-webkit-scrollbar-track': {
                  bgcolor: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                  bgcolor: tokens?.border?.default,
                  borderRadius: 4,
                  '&:hover': {
                    bgcolor: tokens?.text?.muted,
                  },
                },
              }}
            >
              <Table
                stickyHeader
                size="small"
                sx={{
                  width: '100%',
                  tableLayout: 'auto',
                  '& .MuiTableHead-root': {
                    backgroundColor: tokens.surface.elevated,
                    '& .MuiTableCell-head': {
                      color: tokens.text.secondary,
                      fontWeight: 600,
                      borderBottom: `1px solid ${tokens.border.default}`,
                    },
                  },
                  '& .MuiTableBody-root .MuiTableRow-root': {
                    transition: tokens.transitions?.colors,
                    '&:hover': {
                      backgroundColor: tokens.surface.glass,
                    },
                  },
                }}
              >
                <TableHead>
                  <TableRow
                    sx={{
                      '& th': {
                        backgroundColor: tokens.surface.elevated,
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.02em',
                        color: tokens.text.secondary,
                        borderBottom: `1px solid ${tokens.border.default}`,
                        whiteSpace: 'nowrap',
                        py: 1,
                        px: 1,
                      },
                    }}
                  >
                    {/* Checkbox */}
                    <TableCell padding="checkbox" sx={{ width: 36, pl: 1, pr: 0 }}>
                      <Checkbox
                        indeterminate={isIndeterminate}
                        checked={isAllSelected}
                        onChange={selectAll}
                        size="small"
                        sx={{
                          p: 0.5,
                          color: tokens.text.tertiary,
                          '&.Mui-checked, &.MuiCheckbox-indeterminate': {
                            color: tokens.accent.primary,
                          },
                        }}
                      />
                    </TableCell>
                    {/* Icon */}
                    <TableCell padding="none" sx={{ width: 36, px: 0.25 }} />
                    {/* ID */}
                    <TableCell sx={{ width: 50, pl: 0.5, pr: 1 }}>
                      <TableSortLabel
                        active={orderBy === 'id'}
                        direction={orderBy === 'id' ? order : 'asc'}
                        onClick={() => handleSort('id')}
                      >
                        ID
                      </TableSortLabel>
                    </TableCell>
                    {/* Name */}
                    <TableCell sx={{ minWidth: 180, maxWidth: 280, px: 1 }}>
                      <TableSortLabel
                        active={orderBy === 'name'}
                        direction={orderBy === 'name' ? order : 'asc'}
                        onClick={() => handleSort('name')}
                      >
                        {t('sharedName')}
                      </TableSortLabel>
                    </TableCell>
                    {/* Group */}
                    <TableCell sx={{ width: 90, px: 1 }}>
                      <TableSortLabel
                        active={orderBy === 'groupName'}
                        direction={orderBy === 'groupName' ? order : 'asc'}
                        onClick={() => handleSort('groupName')}
                      >
                        <FolderIcon sx={{ fontSize: 14, mr: 0.5 }} />
                        {t('groupParent')}
                      </TableSortLabel>
                    </TableCell>
                    {/* Driver */}
                    <TableCell sx={{ width: 100, px: 1 }}>
                      <TableSortLabel
                        active={orderBy === 'driverName'}
                        direction={orderBy === 'driverName' ? order : 'asc'}
                        onClick={() => handleSort('driverName')}
                      >
                        <PersonIcon sx={{ fontSize: 14, mr: 0.5 }} />
                        {t('sharedDriver')}
                      </TableSortLabel>
                    </TableCell>
                    {/* Plate */}
                    <TableCell sx={{ width: 110, px: 1 }}>{t('sharedDevicePlate')}</TableCell>
                    {/* Status */}
                    <TableCell align="center" sx={{ width: 170, px: 0.5 }}>
                      <TableSortLabel
                        active={orderBy === 'status'}
                        direction={orderBy === 'status' ? order : 'asc'}
                        onClick={() => handleSort('status')}
                      >
                        {t('deviceStatus')}
                      </TableSortLabel>
                    </TableCell>
                    {/* Last Update */}
                    <TableCell sx={{ width: 85, px: 1 }}>
                      <TableSortLabel
                        active={orderBy === 'lastUpdate'}
                        direction={orderBy === 'lastUpdate' ? order : 'asc'}
                        onClick={() => handleSort('lastUpdate')}
                      >
                        {t('deviceLastUpdate')}
                      </TableSortLabel>
                    </TableCell>
                    {/* Users */}
                    {manager && (
                      <TableCell align="center" sx={{ width: 50, px: 0.5 }}>
                        <Tooltip title={t('settingsUsers')}>
                          <PeopleIcon sx={{ fontSize: 16, verticalAlign: 'middle', color: 'text.secondary' }} />
                        </Tooltip>
                      </TableCell>
                    )}
                    {/* Actions */}
                    <TableCell align="right" sx={{ width: 80, pr: 1 }}>{t('sharedActions')}</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={manager ? 11 : 10} align="center" sx={{ py: 6 }}>
                        <CircularProgress size={28} />
                      </TableCell>
                    </TableRow>
                  ) : paginatedDevices.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={manager ? 11 : 10} sx={{ py: 0, border: 'none' }}>
                        {/* Empty State - Componente Genérico */}
                        <PremiumEmptyState
                          icon={DirectionsCarIcon}
                          title={t('devicesEmpty') || 'Nenhum dispositivo cadastrado'}
                          titleFiltered={t('devicesNotFound') || 'Nenhum dispositivo encontrado'}
                          subtitle={t('devicesEmptyHint') || 'Comece adicionando seu primeiro dispositivo para monitorar sua frota'}
                          subtitleFiltered={t('devicesNotFoundHint') || 'Tente ajustar os filtros de busca ou limpar a seleção'}
                          hasFilters={hasActiveFilters}
                          onClearFilters={clearFilters}
                          addPath="/settings/device"
                          addLabel={t('deviceAdd') || 'Adicionar Dispositivo'}
                        />
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedDevices.map((row) => (
                      <DevicesTableRow
                        key={row.id}
                        row={row}
                        selected={isSelected(row.id)}
                        onSelect={toggleSelect}
                        onRefresh={refresh}
                        onNavigate={navigate}
                        showUsers={manager}
                      />
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination - Fixed at bottom */}
            <Box
              sx={{
                flexShrink: 0,
                borderTop: `1px solid ${tokens.border.subtle}`,
                backgroundColor: tokens.isDark
                  ? 'rgba(22, 27, 34, 0.95)'
                  : 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(8px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                px: { xs: 1, sm: 2 },
                py: 0.5,
              }}
            >
              <TablePagination
                component="div"
                count={filteredDevices.length}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
                rowsPerPageOptions={isMobile ? [10, 25, 50] : [10, 25, 50, 100]}
                labelRowsPerPage={t('sharedRowsPerPage')}
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} ${t('sharedOf')} ${count}`}
                sx={{
                  '& .MuiTablePagination-toolbar': {
                    minHeight: { xs: 40, sm: 44 },
                    px: 0,
                  },
                  '& .MuiTablePagination-selectLabel': {
                    display: { xs: 'none', sm: 'block' },
                    color: tokens.text.secondary,
                    fontSize: '0.8125rem',
                  },
                  '& .MuiTablePagination-displayedRows': {
                    color: tokens.text.primary,
                    fontSize: '0.8125rem',
                    fontWeight: 500,
                  },
                  '& .MuiTablePagination-select': {
                    color: tokens.text.primary,
                    fontWeight: 500,
                  },
                  '& .MuiTablePagination-selectIcon': {
                    color: tokens.text.secondary,
                  },
                  '& .MuiIconButton-root': {
                    color: tokens.text.secondary,
                    borderRadius: tokens.radius.md,
                    transition: tokens.transitions.all,
                    '&:hover': {
                      backgroundColor: tokens.accent.subtle,
                      color: tokens.accent.primary,
                    },
                    '&.Mui-disabled': {
                      color: tokens.text.muted,
                      opacity: 0.5,
                    },
                  },
                }}
              />
            </Box>
          </Paper>
        </Box>
      </Box>

      {/* Delete Confirmation Dialog - Componente Genérico */}
      <BulkDeleteDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleBulkDelete}
        selectedCount={selectedIds.length}
        loading={bulkActionLoading}
        entityName={t('sharedDevices') || 'dispositivo(s)'}
      />

      {/* Move to Group Menu */}
      <Menu
        anchorEl={moveMenuAnchor}
        open={Boolean(moveMenuAnchor)}
        onClose={() => setMoveMenuAnchor(null)}
        PaperProps={{
          sx: {
            bgcolor: tokens.surface.elevated,
            borderRadius: tokens.radius.lg,
            border: `1px solid ${tokens.border.subtle}`,
            minWidth: 200,
          },
        }}
      >
        <MenuItem
          onClick={() => handleBulkMoveToGroup(null)}
          disabled={bulkActionLoading}
          sx={{ color: tokens.text.secondary }}
        >
          <FolderIcon sx={{ mr: 1, fontSize: 18 }} />
          {t('sharedNoGroup') || 'Sem Grupo'}
        </MenuItem>
        {availableGroups.map((group) => (
          <MenuItem
            key={group.id}
            onClick={() => handleBulkMoveToGroup(group.id)}
            disabled={bulkActionLoading}
            sx={{ color: tokens.text.primary }}
          >
            <FolderIcon sx={{ mr: 1, fontSize: 18, color: tokens.accent.primary }} />
            {group.name}
          </MenuItem>
        ))}
      </Menu>

      {/* Export Menu */}
      <Menu
        anchorEl={exportMenuAnchor}
        open={Boolean(exportMenuAnchor)}
        onClose={() => setExportMenuAnchor(null)}
        PaperProps={{
          sx: {
            bgcolor: tokens.surface.elevated,
            borderRadius: tokens.radius.lg,
            border: `1px solid ${tokens.border.subtle}`,
          },
        }}
      >
        <MenuItem
          onClick={() => { handleExportCsv(); setExportMenuAnchor(null); }}
          sx={{ color: tokens.text.primary }}
        >
          <FileDownloadIcon sx={{ mr: 1, fontSize: 18, color: tokens.semantic.success }} />
          CSV
        </MenuItem>
        <MenuItem
          onClick={() => { handleExportXlsx(); setExportMenuAnchor(null); }}
          sx={{ color: tokens.text.primary }}
        >
          <TableChartIcon sx={{ mr: 1, fontSize: 18, color: tokens.semantic.info }} />
          Excel
        </MenuItem>
        <MenuItem
          onClick={() => { handleExportPdf(); setExportMenuAnchor(null); }}
          sx={{ color: tokens.text.primary }}
        >
          <PictureAsPdfIcon sx={{ mr: 1, fontSize: 18, color: tokens.semantic.error }} />
          PDF
        </MenuItem>
      </Menu>

      {/* Bulk Connections Dialog - Vincular/Desvincular em massa */}
      <BulkConnectionsDialog
        open={connectionsDialogOpen}
        onClose={() => setConnectionsDialogOpen(false)}
        selectedIds={selectedIds}
        entityType="device"
        entityName={t('sharedDevices') || 'dispositivos'}
        onSuccess={() => {
          // Re-fetch driver links for visible devices (forceRefresh clears cache internally)
          const deviceIds = paginatedDevices.map((d) => d.id);
          fetchDriverLinksForDevices(deviceIds, true);
          clearSelection();
        }}
      />
    </PageLayout>
  );
};

export default DevicesPage;
